/**
 * Rahmen der Buchhaltung: links die Seitenleiste, rechts der Inhalt.
 *
 * Aufbau uebernommen aus `finance-web/src/components/layout/FinanceLayout.tsx`
 * — dieselbe einklappbare Leiste, dieselben Farben, derselbe Fuss mit Name,
 * Konto-Links und Abmelden. Wer zwischen Finanzen und Buchhaltung wechselt,
 * soll nichts umlernen muessen.
 *
 * Die acht Eintraege sind die Bereiche aus dem Bauplan (§4–§8). Ihre Seiten
 * sind in dieser Nacht leer; sie tragen den Namen, unter dem der jeweilige
 * Bauabschnitt sie fuellt.
 */

import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { kontoUrl } from '@/lib/konto';
import { IST_MOCK } from '@/lib/api';
import { StatusPunkt } from './StatusPunkt';
import {
  Banknote,
  BookOpenCheck,
  Camera,
  ClipboardList,
  Inbox,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Settings,
  Truck,
  Wallet,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/features/auth/AuthContext';

const NAVIGATION = [
  { to: '/eingang', label: 'Eingang', icon: Inbox },
  { to: '/zu-zahlen', label: 'Zu zahlen', icon: Wallet },
  { to: '/pruefliste', label: 'Prüfliste', icon: ClipboardList },
  { to: '/bank', label: 'Bank', icon: Banknote },
  { to: '/scan', label: 'Scan', icon: Camera },
  { to: '/review', label: 'Review', icon: BookOpenCheck },
  { to: '/lieferanten', label: 'Lieferanten', icon: Truck },
  { to: '/einstellungen', label: 'Einstellungen', icon: Settings },
];

const STORAGE_KEY = 'buchhaltung_sidebar_collapsed';

function gespeichertEingeklappt(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function BuchhaltungLayout() {
  const { user, logout } = useAuth();
  const [eingeklappt, setEingeklappt] = useState(gespeichertEingeklappt);

  const umschalten = () => {
    setEingeklappt((bisher) => {
      const neu = !bisher;
      try {
        localStorage.setItem(STORAGE_KEY, neu ? '1' : '0');
      } catch {
        // localStorage kann fehlen (z. B. private Fenster) — dann gilt der Zustand nur bis zum Reload.
      }
      return neu;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-30 flex flex-col border-r border-slate-200 bg-white transition-[width] duration-200',
          eingeklappt ? 'w-16' : 'w-60'
        )}
      >
        <div className={clsx('flex items-center py-5', eingeklappt ? 'justify-center px-0' : 'gap-2 px-5')}>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-white">
            <Receipt size={18} />
          </span>
          {!eingeklappt && (
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-900">elbklar</div>
              <div className="text-xs text-slate-500">Buchhaltung</div>
            </div>
          )}
        </div>

        <nav className={clsx('flex-1 space-y-0.5 py-2', eingeklappt ? 'px-2' : 'px-3')}>
          {NAVIGATION.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={eingeklappt ? label : undefined}
              className={({ isActive }) =>
                clsx(
                  'flex items-center rounded-lg py-2 text-sm transition-colors',
                  eingeklappt ? 'justify-center px-0' : 'gap-3 px-3',
                  isActive
                    ? 'bg-primary-50 font-medium text-primary-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )
              }
            >
              <Icon size={17} className="shrink-0" />
              {!eingeklappt && label}
            </NavLink>
          ))}
        </nav>

        <div className={clsx('border-t border-slate-200 py-3', eingeklappt ? 'px-2' : 'px-4')}>
          {eingeklappt ? (
            <div className="flex flex-col items-center gap-1">
              <a
                href={kontoUrl('/profil')}
                title={`Konto (${user?.name || user?.email || 'nicht angemeldet'})`}
                className="flex w-full items-center justify-center rounded-lg py-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700"
              >
                <span className="text-xs font-semibold">{initialen(user?.name)}</span>
              </a>
              <button
                type="button"
                onClick={() => void logout()}
                title="Abmelden"
                className="flex w-full items-center justify-center rounded-lg py-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <>
              {/* Der eigene Name ist der Weg ins Konto — dieselbe Stelle und
                  dasselbe Wort wie in crm, pm und finanzen. Die persoenlichen
                  Einstellungen liegen zentral auf konto.elbklar.com, nicht
                  hier; `/einstellungen` in der Leiste oben ist etwas anderes,
                  naemlich die Einstellungen der Buchhaltung selbst. */}
              <a
                href={kontoUrl('/profil')}
                className="block truncate text-xs font-medium text-slate-600 transition-colors hover:text-slate-900"
              >
                {user?.name || user?.email || 'Nicht angemeldet'}
              </a>
              <a
                href={kontoUrl('/profil')}
                className="mt-1.5 block text-xs text-slate-400 transition-colors hover:text-slate-700"
              >
                Konto
              </a>
              <a
                href={kontoUrl('/benachrichtigungen')}
                className="mt-0.5 block text-xs text-slate-400 transition-colors hover:text-slate-700"
              >
                Benachrichtigungen
              </a>
              <button
                type="button"
                onClick={() => void logout()}
                className="mt-0.5 block text-xs text-slate-400 transition-colors hover:text-slate-700"
              >
                Abmelden
              </button>
            </>
          )}
          <button
            type="button"
            onClick={umschalten}
            title={eingeklappt ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
            className={clsx(
              'mt-2 flex items-center rounded-lg py-2 text-xs text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700',
              eingeklappt ? 'w-full justify-center' : 'gap-2 px-0'
            )}
          >
            {eingeklappt ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            {!eingeklappt && 'Einklappen'}
          </button>
        </div>
      </aside>

      <main className={clsx('min-h-screen transition-[margin] duration-200', eingeklappt ? 'ml-16' : 'ml-60')}>
        {/* Die Kopfzeile traegt genau eine Auskunft: laeuft der Belegeingang
            noch? Sie steht hier und nicht auf einer Seite, weil ein stiller
            Ausfall sonst nur auffiele, wenn man zufaellig die richtige Seite
            offen hat (Ablaeufe, Regel 5). */}
        <header className="sticky top-0 z-20 flex items-center justify-end gap-4 border-b border-slate-200 bg-white/90 px-8 py-2.5 backdrop-blur">
          {IST_MOCK && (
            <span
              className="mr-auto inline-flex items-center gap-1.5 rounded-full border border-warning-200 bg-warning-50 px-2.5 py-0.5 text-xs font-medium text-warning-700"
              title="VITE_MOCK_API=1 — die Seiten laufen gegen Beispieldaten aus lib/mock.ts, nicht gegen die API."
            >
              Beispieldaten
            </span>
          )}
          <StatusPunkt />
        </header>

        <div className="mx-auto max-w-[1400px] px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function initialen(name: string | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((teil) => teil[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
